import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  buildToken,
  createChallenge,
  isExpired,
  matches,
  CAPTCHA_TTL_MS,
} from "../../utils/captcha";

const WIDTH = 190;
const HEIGHT = 56;

function draw(canvas, text) {
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, WIDTH, HEIGHT);

  ctx.fillStyle = "#EBEBED";
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  for (let i = 0; i < 6; i += 1) {
    ctx.strokeStyle = `rgba(6, 27, 43, ${0.15 + Math.random() * 0.25})`;
    ctx.lineWidth = 1 + Math.random();
    ctx.beginPath();
    ctx.moveTo(Math.random() * WIDTH, Math.random() * HEIGHT);
    ctx.lineTo(Math.random() * WIDTH, Math.random() * HEIGHT);
    ctx.stroke();
  }

  for (let i = 0; i < 40; i += 1) {
    ctx.fillStyle = `rgba(6, 27, 43, ${0.1 + Math.random() * 0.3})`;
    ctx.fillRect(Math.random() * WIDTH, Math.random() * HEIGHT, 2, 2);
  }

  const step = WIDTH / (text.length + 1);
  text.split("").forEach((char, index) => {
    ctx.save();
    ctx.translate(step * (index + 1), HEIGHT / 2 + (Math.random() * 8 - 4));
    ctx.rotate((Math.random() * 40 - 20) * (Math.PI / 180));
    ctx.font = `bold ${24 + Math.random() * 8}px Arial`;
    ctx.fillStyle = "#061B2B";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(char, 0, 0);
    ctx.restore();
  });
}

function Captcha({
  onVerify,
  label = "Digite os caracteres da imagem",
  placeholder = "Ex.: A1B2C",
  error,
  disabled = false,
}) {
  const canvasRef = useRef(null);
  const [challenge, setChallenge] = useState(createChallenge);
  const [answer, setAnswer] = useState("");
  const [verified, setVerified] = useState(false);

  // Mantém a callback em uma ref para o refresh não mudar de identidade a cada
  // render (os pais passam arrow functions inline).
  const onVerifyRef = useRef(onVerify);
  useEffect(() => {
    onVerifyRef.current = onVerify;
  });

  const refresh = useCallback(() => {
    setChallenge(createChallenge());
    setAnswer("");
    setVerified(false);
    onVerifyRef.current(null);
  }, []);

  useEffect(() => {
    if (canvasRef.current) draw(canvasRef.current, challenge.text);
  }, [challenge]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (isExpired(challenge)) refresh();
    }, 10000);
    return () => clearInterval(timer);
  }, [challenge, refresh]);

  const handleChange = (event) => {
    const value = event.target.value.slice(0, challenge.text.length);
    setAnswer(value);

    const solved = matches(challenge, value);
    setVerified(solved);
    onVerify(solved ? buildToken(challenge, value) : null);
  };

  return (
    <div style={{ marginBottom: "15px" }}>
      <p className="label">{label} *</p>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <canvas
          ref={canvasRef}
          width={WIDTH}
          height={HEIGHT}
          aria-label="Imagem do captcha"
          style={{ borderRadius: "4px", border: "1px solid #EBEBED" }}
        />
        <button
          type="button"
          onClick={refresh}
          disabled={disabled}
          title={`O código expira em ${CAPTCHA_TTL_MS / 60000} minutos`}
          style={{
            border: "1px solid #EBEBED",
            backgroundColor: "#fff",
            borderRadius: "4px",
            cursor: disabled ? "not-allowed" : "pointer",
            fontSize: "18px",
            height: "36px",
            width: "36px",
          }}
        >
          ↻
        </button>
      </div>
      <input
        className={`input ${error ? "inputDanger" : ""}`}
        type="text"
        value={answer}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        maxLength={challenge.text.length}
        style={{ marginTop: "8px", textTransform: "uppercase" }}
      />
      {verified && (
        <span
          style={{ color: "#1c787e", fontSize: "12px", fontWeight: "bold" }}
        >
          Código verificado
        </span>
      )}
      {error && !verified && (
        <span style={{ color: "#FF460C", fontSize: "12px", display: "block" }}>
          {error}
        </span>
      )}
    </div>
  );
}

export default Captcha;
